<template>
  <AdminLayout>
    <Head title="Company Users" />

    <template #pageTitle> Users </template>

    <template #pageTitleWidgets>
      <DataGridControlBar />
    </template>

    <template #headingOptions>
      <button
        @click="showingInviteModal = true"
        type="button"
        class="inline-flex items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
      >
        <PlusSmIconSolid
          class="-ml-0.5 mr-2 h-5 w-5"
          aria-hidden="true"
        />
        Invite User
      </button>
    </template>

    <DataGrid
      name="users_data_table"
      :endpoint="route('api.tables.users.index')"
      rowId="id"
      :filters="form"
      :columns="columns"
      :block-size="40"
      :page-size="40"
    />

    <template #modals>
      <SmallModal
        :is-open="showingInviteModal"
        @close="showingInviteModal = false"
      >
        <template #header> Invite User </template>

        <div class="mt-4">
          <div class="input-group">
            <label for="email"> Email </label>
            <input
              type="text"
              v-model="inviteForm.email"
              name="email"
              id="email"
            />
            <p
              v-if="getErrors('email')"
              class="input-error"
            >
              {{ getErrors('email') }}
            </p>
          </div>

          <div class="input-group mt-2">
            <label for="email"> First Name </label>
            <input
              type="text"
              v-model="inviteForm.first_name"
              name="first_name"
            />
            <p
              v-if="getErrors('first_name')"
              class="input-error"
            >
              {{ getErrors('first_name') }}
            </p>
          </div>

          <div class="input-group mt-2">
            <label for="email"> Last Name </label>
            <input
              type="text"
              v-model="inviteForm.last_name"
              name="last_name"
            />
            <p
              v-if="getErrors('last_name')"
              class="input-error"
            >
              {{ getErrors('last_name') }}
            </p>
          </div>

          <div class="input-group mt-2">
            <label for="birthdate"> Birthdate </label>
            <DatePicker
              v-model="inviteForm.birthdate"
              color="gray"
              title-position="right"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <div class="relative">
                  <CalendarIcon
                    class="top-3 left-4 h-5 w-5 text-gray-800 absolute"
                    aria-hidden="true"
                  />
                  <input
                    class="inline-flex items-center w-full justify-center pl-10 border border-gray-400 rounded-md px-4 h-[42px] bg-gray-100 text-sm font-medium text-gray-800 cursor-pointer"
                    id="birthdate"
                    :value="inputValue"
                    v-on="inputEvents"
                    autocomplete="birthdate"
                  />
                </div>
              </template>
            </DatePicker>
            <p
              v-if="getErrors('birthdate')"
              class="input-error"
            >
              {{ getErrors('birthdate') }}
            </p>
          </div>

          <div class="input-group mt-2">
            <label for="roles"> Roles </label>
            <MultiSelectInput
              v-model="inviteForm.roles"
              :options="roleOptions"
            >
              Select roles
            </MultiSelectInput>
            <p
              v-if="getErrors('roles')"
              class="input-error"
            >
              {{ getErrors('roles') }}
            </p>
          </div>

          <div class="input-group mt-4">
            <label
              for="should_send_email"
              class="font-medium text-gray-700"
            >
              <input
                v-model="inviteForm.should_send_email"
                :disabled="hasGuestRole"
                name="should_send_email"
                type="checkbox"
                class="focus:ring-blue-600 text-blue-600 w-5 h-5 mr-2 border-gray-400"
              />

              Send Email Invite?
            </label>
            <p
              v-if="getErrors('should_send_email')"
              class="input-error"
            >
              {{ getErrors('should_send_email') }}
            </p>
          </div>
        </div>

        <template #footer>
          <button
            type="button"
            class="inline-flex items-center px-4 py-2 mr-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
            @click="submitInvite()"
          >
            Invite
          </button>
          <button
            type="button"
            class="btn btn-secondary ml-3"
            @click="showingInviteModal = false"
          >
            Cancel
          </button>
        </template>
      </SmallModal>

      <SmallModal
        :is-open="showingRemoveUser"
        @close="showingRemoveUser = false"
      >
        <template #header> Remove User </template>

        <div class="mt-4">
          <div class="input-group">
            <label> You are about to remove the following user: </label>
            <p class="mt-2 font-bold">
              {{ userToRemove.email }}
            </p>
          </div>
        </div>

        <template #footer>
          <button
            type="button"
            class="btn btn-danger"
            @click="removeUser()"
          >
            Remove
          </button>
          <button
            type="button"
            class="btn btn-secondary ml-3"
            @click="showingRemoveUser = false"
          >
            Cancel
          </button>
        </template>
      </SmallModal>

      <SmallModal
        :is-open="showingResendInvite"
        @close="showingResendInvite = false"
      >
        <template #header> Resend Invite </template>

        <div class="mt-4">
          <div class="input-group">
            <label for="email"> Email </label>
            <p>
              {{ userToResendInvite.email }}
            </p>
          </div>
          <div class="input-group mt-3">
            <label for="email"> Role </label>
            <p>
              {{ userToResendInvite.company_role }}
            </p>
          </div>
        </div>

        <template #footer>
          <button
            type="button"
            class="btn btn-primary"
            @click="resendInvite()"
          >
            Re-send
          </button>
          <button
            type="button"
            class="btn btn-secondary ml-3"
            @click="showingResendInvite = false"
          >
            Cancel
          </button>
        </template>
      </SmallModal>
    </template>
  </AdminLayout>
</template>
<script>
import { ref } from 'vue'
import Pagination from '@/Components/Pagination.vue'
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { Head, Link, usePage, useForm } from '@inertiajs/vue3'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { DotsHorizontalIcon } from '@heroicons/vue/solid'
import SmallModal from '@/Components/Modals/SmallModal.vue'
import dateFilters from '@/Mixins/dateFilters'
import isUrlAware from '@/Mixins/isUrlAware'
import { PlusSmIcon as PlusSmIconSolid } from '@heroicons/vue/solid'
import { CalendarIcon, PlusSmIcon as PlusSmIconOutline } from '@heroicons/vue/outline'
import DataTable from '@/Components/Table/DataTable'
import IndexRow from '@/Pages/Company/Users/Components/IndexRow'
import DataGrid from '@/Components/DataGrid/Tables/Generic.vue'
import LinkCell from '@/Components/DataGrid/Cells/LinkCell.vue'
import DateCell from '@/Components/DataGrid/Cells/DateCell.vue'
import DataGridControlBar from '@/Components/DataGrid/Controls/ControlBar.vue'
import { useInitGridState } from '@/Components/DataGrid/Composables/grid-state'
import capitalize from '@/Filters/Capitalize'
import MultiSelectInput from '@/Components/Forms/MultiSelectInput.vue'
import { wrapWithFlag } from '@/Components/Flags'
import { DatePicker } from 'v-calendar'

export default {
  mixins: [dateFilters, isUrlAware],

  components: {
    CalendarIcon,
    AdminLayout,
    Head,
    Link,
    Pagination,
    DataGrid,
    DataGridControlBar,
    DotsHorizontalIcon,
    Popover,
    PopoverButton,
    PopoverPanel,
    SmallModal,
    PlusSmIconOutline,
    PlusSmIconSolid,
    DataTable,
    IndexRow,
    LinkCell,
    DateCell,
    MultiSelectInput,
    DatePicker
  },

  props: {
    company: Object,
    roles: Object,
    errors: Object
  },

  setup(props) {
    const showingInviteModal = ref(false)
    const showingRemoveUser = ref(false)
    const showingResendInvite = ref(false)
    const userToRemove = ref(null)
    const userToResendInvite = ref(null)

    const inviteForm = useForm({
      email: '',
      first_name: '',
      last_name: '',
      birthdate: null,
      roles: [],
      should_send_email: true
    })

    const removeUserForm = useForm({})
    const resendInviteForm = useForm({})

    useInitGridState()

    return {
      showingInviteModal,
      showingRemoveUser,
      showingResendInvite,
      inviteForm,
      removeUserForm,
      resendInviteForm,
      userToRemove,
      userToResendInvite,
      form: {},
      columns: [
        {
          headerName: 'Email',
          field: 'email',
          cellRenderer: wrapWithFlag(LinkCell),
          cellRendererParams: (params) => ({
            showFlag: Boolean(params.data.flags?.length),
            url: `/company/users/${params.data.id}/edit`,
            display: params.value
          }),
          filter: 'agTextColumnFilter'
        },
        {
          headerName: 'Roles',
          field: 'role_names',
          sortable: false,
          valueFormatter: (params) => params.value.map((value) => capitalize(value)).join(', '),
          filter: 'agSetColumnFilter',
          filterParams: {
            values: Object.values(props.roles)
          }
        },
        {
          headerName: 'First Name',
          field: 'first_name',
          filter: 'agTextColumnFilter'
        },
        {
          headerName: 'Last Name',
          field: 'last_name',
          filter: 'agTextColumnFilter',
          sort: 'asc'
        },
        {
          headerName: 'Phone',
          field: 'phone',
          filter: 'agTextColumnFilter'
        },
        {
          headerName: 'Creation Date',
          field: 'created_at',
          cellRenderer: 'DateCell',
          sortable: false
        },
        {
          headerName: 'Updated Date',
          field: 'updated_at',
          cellRenderer: 'DateCell',
          sortable: false
        }
      ],
      dataGridState: null
    }
  },

  computed: {
    roleOptions() {
      return Object.keys(this.roles).map((value) => {
        return { label: this.roles[value], value: value }
      })
    },
    hasGuestRole() {
      return this.inviteForm.roles.includes('guest')
    }
  },

  watch: {
    'inviteForm.roles'(newValue) {
      if (newValue.includes('guest')) {
      }
    },
    hasGuestRole(value) {
      if (value) {
        this.inviteForm.should_send_email = false
      }
    }
  },

  methods: {
    submitInvite() {
      this.inviteForm.post(route('company.users.store'), {
        onSuccess: () => {
          this.inviteForm.reset()
        }
      })
    },
    getErrors(field, bag = null) {
      let errors = bag ? this.errors[bag] || {} : this.errors

      if (field in errors) {
        return errors[field]
      }

      return ''
    },
    openRemovePrompt(user) {
      this.userToRemove = user
      this.showingRemoveUser = true
    },
    removeUser() {
      if (!this.userToRemove) {
        return
      }

      this.removeUserForm.delete(route('company.users.destroy', { user: this.userToRemove.id }), {
        onSuccess: () => {
          this.showingRemoveUser = false
        }
      })
    },
    openResendInvitePrompt(user) {
      this.userToResendInvite = user
      this.showingResendInvite = true
    },
    resendInvite() {
      if (!this.userToResendInvite) {
        return
      }

      this.resendInviteForm.post(route('company.users.invite.store', { user: this.userToResendInvite.id }), {
        onSuccess: () => {
          this.showingResendInvite = false
        }
      })
    }
  }
}
</script>
